/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  BuilderPermission,
  ScheduleDetailsDto,
  ScheduleDetailsTaskDto,
  ScheduleJobProgressFilterDto,
  ScheduleType,
  UserListItemDto,
} from '@gh/builder-shared-data';
import {
  EMPTY_FORM_STATE_SNAPSHOT,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
} from '@gh/common-shared';
import { EMPTY_ID, EntityVersion, ID, WithDirty, WithVersion } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { cast } from '@gh/core-util';
import { GenericScheduleTaskInfo, recalculateScheduleTasks } from '@gh/builder-shared-gantt-chart';

import { isEmpty, omit, pick } from 'lodash';

export type ScheduleDetailsTimePeriod = {
  startDate: Date;
  endDate: Date;
  baseStartDate: Date;
  baseEndDate: Date;
  duration: number;
};
export type ScheduleMasterInfo = {
  salesFolderName: string;
  salesFolderAddedDate: Date;
  customerName: string;
  name: string;
  type: ScheduleType;
  jobNumber: string;
  drawRuleCode?: string;
  drawRuleName?: string;
  siteAddress: string;
  isSendReportToSupervisor: boolean;
  financeApprovedDate: Date;
  councilApprovedDate: Date;
  authToCommenceDate: Date;
  contractStartDate: Date;
  isOverridden: boolean;
};

export type ScheduleTaskConfirmed = {
  confirmedWith?: string;
  confirmedOnDate?: Date;
  confirmedForDate?: Date;
};

export type ScheduleJobProgressFilterDialogConfig = {
  type: number;
};

export enum ScheduleJobProgressFilterSupervisorType {
  All = 'All',
  Selected = 'Selected',
}

export type ScheduleJobProgressUserAutocompleteItem = UserListItemDto;

export type ScheduleJobProgressFilter = ScheduleJobProgressFilterDto & {
  supervisorType: ScheduleJobProgressFilterSupervisorType; // @toodo: maybe unnecessary type? allSupervisors
  supervisor: ScheduleJobProgressUserAutocompleteItem;
};

export const scheduleJobProgressFilterToDto = (details: ScheduleJobProgressFilter): ScheduleJobProgressFilterDto =>
  ({
    ...<ScheduleJobProgressFilterDto>omit(details, ['supervisorType', 'supervisor']),
    supervisorId: isEmpty(details.supervisorType) || details.supervisorType === ScheduleJobProgressFilterSupervisorType.Selected
      ? details.supervisor.userId
      : void 0,
    allSupervisors: details.supervisorType === ScheduleJobProgressFilterSupervisorType.All,
  });

export const scheduleJobProgressFilterFromDto = (dto: ScheduleJobProgressFilterDto): ScheduleJobProgressFilter => ({
  ...<ScheduleJobProgressFilter>omit(dto, ['allSupervisors']),
  supervisorType: dto.allSupervisors
    ? ScheduleJobProgressFilterSupervisorType.All
    : ScheduleJobProgressFilterSupervisorType.Selected,
});

export type ScheduleTask = WithDirty<ScheduleDetailsTaskDto & GenericScheduleTaskInfo & ScheduleTaskConfirmed>;
export type ScheduleDetails = WithVersion<{
  scheduleId: ID;
  jobId: ID;
  salesFolderId: ID;
  templateId: ID;
  type: ScheduleType;
  masterInfo: FormStateSnapshot<ScheduleMasterInfo>;
  time: ScheduleDetailsTimePeriod;
  tasks: ScheduleTask[];
  gDriveUrl: ID;
  isFinalized: boolean;
  isNotProceeding: boolean;
  isSalesFolderActive: boolean;
  showBaseDates: boolean;
  templateVersion?: EntityVersion;
}>;

export const SCHEDULE_DETAILS_INIT: ScheduleDetails = {
  scheduleId: EMPTY_ID,
  jobId: EMPTY_ID,
  templateId: EMPTY_ID,
  salesFolderId: EMPTY_ID,
  type: ScheduleType.Construction,
  masterInfo: EMPTY_FORM_STATE_SNAPSHOT,
  time: <any>{},
  tasks: [],
  gDriveUrl: '',
  isFinalized: false,
  isNotProceeding: false,
  isSalesFolderActive: false,
  showBaseDates: false,
};

export const scheduleDetailsFromDto = (dto: ScheduleDetailsDto): ScheduleDetails => ({
  scheduleId: dto.scheduleId,
  templateId: dto.templateId,
  jobId: dto.jobId,
  salesFolderId: dto.salesFolderId,
  type: dto.type,
  masterInfo: formStateSnapshot(<ScheduleMasterInfo>pick(dto, [
    'jobNumber', 'name', 'type', 'jobNumber', 'drawRuleId', 'drawRuleName', 'siteAddress', 'customerName',
    'isSendReportToSupervisor', 'financeApprovedDate', 'councilApprovedDate', 'authToCommenceDate',
    'contractStartDate', 'isOverridden', 'salesFolderName', 'salesFolderAddedDate', 'drawRuleCode',
  ])),
  time: {
    startDate: dto.startDate,
    endDate: dto.endDate,
    baseStartDate: dto.baseStartDate,
    baseEndDate: dto.baseEndDate,
    duration: dto.duration,
  },
  tasks: <ScheduleTask[]>recalculateScheduleTasks(dto.tasks.map((task) => ({
    ...task,
    canBeDeleted: true,
    hasChildren: false,
    canChangeCompletionPct: true,
  }))),
  gDriveUrl: dto.gDriveUrl,
  isFinalized: dto.isFinalized,
  isNotProceeding: dto.isNotProceeding,
  isSalesFolderActive: dto.isSalesFolderActive,
  version: dto.version,
  templateVersion: dto.templateVersion,
  showBaseDates: false,
});

export const scheduleDetailsToDto = (model: ScheduleDetails,
                                     formMode: FormMode = FormMode.EditMode): ScheduleDetailsDto =>
  cast<ScheduleDetailsDto>({
    scheduleId: model.scheduleId,
    templateId: model.templateId,
    jobId: model.jobId,
    salesFolderId: model.salesFolderId,
// @ts-expect-error Legacy typing suppression
    type: model.type,
    ...getFormStateSnapshotValue(model.masterInfo),
    ...model.time,
    tasks: model.tasks.map((task) => omit(task, [
      'canBeDeleted', 'hasChildren', 'isDirty', 'canChangeCompletionPct',
    ])),
    gDriveUrl: model.gDriveUrl,
    isFinalized: model.isFinalized,
    isNotProceeding: model.isNotProceeding,
    isSalesFolderActive: model.isSalesFolderActive,
    version: model.version,
    templateVersion: model.templateVersion,
  });

/**
 * Returns whether given schedule can be modified.
 */
export const isScheduleEditable = (schedule: ScheduleDetails) => {
  switch (schedule.type) {
    case ScheduleType.Construction:
    case ScheduleType.PreConstruction:
      return !(schedule.isFinalized || schedule.isNotProceeding);
    case ScheduleType.Maintenance:
      return !schedule.isNotProceeding;
    case ScheduleType.Sales:
      return schedule.isSalesFolderActive;
    default:
      return true;
  }
};

export const getScheduleEditPermission = (schedule: ScheduleDetails) => ({
  [ScheduleType.Construction]: BuilderPermission.Schedules.Construction.EDIT,
  [ScheduleType.Sales]: BuilderPermission.Schedules.Sales.EDIT,
  [ScheduleType.PreConstruction]: BuilderPermission.Schedules.PreConstruction.EDIT,
  [ScheduleType.Maintenance]: BuilderPermission.Schedules.Maintenance.EDIT,
})[schedule.type];
